import { Charge } from '../types/planTypes';

/**
 * Utility functions for formatting and displaying plan and charge data
 */

/**
 * Format a charge's price for display
 */
export const formatPrice = (charge: Charge) => {
  // Handle empty charge type
  if (charge.type === 'empty') return 'No pricing data';
  
  // Don't show price for freemium charges
  if (charge.type === 'freemium') return '';
  
  // If type is pricing_not_disclosed, always show "Contact Us" regardless of price
  if (charge.type === 'pricing_not_disclosed' || charge.type === 'pricing_not_diclosed') return 'Contact Us';

  // Otherwise, handle based on price
  if (!charge.price && charge.price !== 0) return 'Contact';
  if (charge.price === 0) return 'Free';

  // Replace USD with $ symbol
  let currency = charge.currency || '$';
  if (currency === 'USD') {
    currency = '$';
  }

  // For percentage, show after the price (20% instead of %20)
  if (currency === '%') {
    return `${charge.price}${currency}${charge.price_per ? ` ${charge.price_per}` : ''}`;
  } else {
    return `${currency}${charge.price}${charge.price_per ? ` ${charge.price_per}` : ''}`;
  }
};

/**
 * Get a display name for a charge type
 */
export const getChargeTypeDisplay = (type: string | null) => {
  if (!type) return '';

  const typeMap: Record<string, string> = {
    'monthly_recurring_billed_yearly': 'Yearly billing',
    'monthly_recurring_billed_monthly': 'Monthly billing',
    'pricing_not_disclosed': 'Contact for pricing',
    'pricing_not_diclosed': 'Contact for pricing', // Keep for backward compatibility
    'usage': 'Usage-based',
    'freemium': 'Free',
    'empty': 'No pricing data available'
  };

  return typeMap[type] || type;
};

/**
 * Format a charge name for display (used in delete confirmation)
 */
export const formatChargeName = (charge: Charge): string => {
  if (!charge) return 'this charge';
  
  // If type is pricing_not_disclosed, always show "Contact Us" regardless of price
  if (charge.type === 'pricing_not_disclosed' || charge.type === 'pricing_not_diclosed') return 'Contact Us pricing';
  
  // If no price is available
  if (!charge.price && charge.price !== 0) return charge.price_per || 'Contact pricing';
  
  // If price is zero
  if (charge.price === 0) return 'Free pricing';
  
  // Replace USD with $ symbol
  let currency = charge.currency || '$';
  if (currency === 'USD') {
    currency = '$';
  }
  
  // Format as "Currency + Price + Price Per" with special handling for percentage
  if (currency === '%') {
    return `${charge.price}${currency}${charge.price_per ? ` ${charge.price_per}` : ''}`;
  } else {
    return `${currency}${charge.price}${charge.price_per ? ` ${charge.price_per}` : ''}`;
  }
};
